import React, { useId, type InputHTMLAttributes } from "react";
import clsx from "clsx";

import styles from "./toggle.module.css";

export type ToggleProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "size"
> & {
  /** Controlled checked state */
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Visible label (also used for `htmlFor`) */
  label: string;

  activeText?: string;
  inactiveText?: string;
  /** When false, the Active/Inactive status badge is hidden. Default true. */
  showStatusText?: boolean;
};

export function Toggle({
  checked,
  onCheckedChange,
  label,
  activeText = "Active",
  inactiveText = "Inactive",
  showStatusText = true,
  disabled,
  className,
  id: idProp,
  ...rest
}: ToggleProps) {
  const reactId = useId();
  const id = idProp ?? `toggle-${reactId.replace(/:/g, "")}`;

  return (
    <label
      className={clsx(
        styles.root,
        disabled && styles.rootDisabled,
        className,
      )}
    >
      <input
        id={id}
        type="checkbox"
        className={styles.srOnly}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onCheckedChange(e.target.checked)}
        role="switch"
        aria-checked={checked}
        {...rest}
      />
      <span className={clsx("sub-heading-md", styles.label)}>{label}</span>
      <span
        className={clsx(styles.track, checked && styles.trackOn)}
        aria-hidden
      >
        <span className={clsx(styles.thumb, checked && styles.thumbOn)} />
      </span>
      {showStatusText ? (
        <span
          className={clsx(
            styles.statusText,
            checked ? styles.active : styles.inactive,
          )}
        >
          {checked ? activeText : inactiveText}
        </span>
      ) : null}
    </label>
  );
}
